import { Fragment } from 'react'
import { observer, useLocalObservable } from 'mobx-react-lite'
import { type ActionItem, type IColumn, Tooltip, Typography } from 'shared/ui'
import { SwitchAction } from 'shared/ui/SwitchAction'
import { DayjsFormats, logger } from 'shared/lib'
import dayjs from 'shared/lib/dayjs'
import { uiStore } from 'shared/store/uiStore'
import { type WorkflowRow } from 'entities/Workflow'
import { useWorkflowListContext } from 'pages/workflows/context/workflowListContext'
import { WorkflowStatusBadge } from 'pages/workflows/ui'
import { workflowOperation } from 'pages/workflows/store/operationStore'
import styles from './styles.module.scss'

const toggleWorkflowActive = async (workflow: WorkflowRow): Promise<void> => {
  try {
    const payload = workflow.enabled
      ? await workflowOperation.disable(workflow)
      : await workflowOperation.enable(workflow)
    if (payload) workflow.syncOrigin(payload)
  } catch (error) {
    logger.error(error)
  }
}

const NameCell = observer(({ workflow }: { workflow: WorkflowRow }) => (
  <div className={styles.nameCellWrapper}>
    <Typography
      variant={'body-md-regular'}
      ellipsis
      tooltipProps={{
        label: workflow.name,
      }}
      ariaLabel={'name'}
      className={styles.nameText}
    >
      {workflow.name}
    </Typography>
    <Tooltip label={workflow.isInvalid && 'Suspicious content detected'} placement={'top'}>
      <div className={styles.nameToggle}>
        <SwitchAction
          size={'small'}
          active={workflow.enabled}
          disabled={workflow.isInvalid}
          onTrigger={() => toggleWorkflowActive(workflow)}
          ariaLabel={`WorkflowActiveToggle-${workflow.id}`}
        />
      </div>
    </Tooltip>
  </div>
))

const COLUMNS: IColumn<WorkflowRow>[] = [
  {
    field: 'name',
    name: 'Name',
    width: '100%',
    maxWidth: '300px',
    minWidth: '300px',
    isDisabledChangeVisible: true,
    renderRowCell: ({ workflow }) => <NameCell workflow={workflow} />,
  },
  {
    field: 'status',
    name: 'Status',
    minWidth: '84px',
    maxWidth: '120px',
    isDisabledChangeVisible: true,
    renderRowCell: ({ workflow }) => (
      <Tooltip label={workflow.isInvalid && 'Suspicious content detected'} placement={'top'}>
        <WorkflowStatusBadge isInvalid={workflow.isInvalid} active={workflow.enabled} />
      </Tooltip>
    ),
  },
  {
    field: 'enrolled',
    name: 'Enrolled',
    minWidth: '84px',
    maxWidth: '120px',
    isDisabledChangeVisible: true,
    renderRowCell: ({ workflow }) => workflow.executionsCount,
  },
  {
    field: 'created_at',
    name: 'Created',
    minWidth: '120px',
    maxWidth: '260px',
    renderRowCell: ({ workflow }) => dayjs(workflow.createdAt).format(DayjsFormats.fullWithAtDash2),
  },
  {
    field: 'last_modified',
    name: 'Last modified',
    minWidth: '120px',
    maxWidth: '260px',
    renderRowCell: ({ workflow }) =>
      !!workflow.updatedAt ? (
        dayjs(workflow.updatedAt).format(DayjsFormats.fullWithAtDash2)
      ) : (
        <Fragment />
      ),
  },
]

export const useWorkflowTable = () => {
  const { items, onSuccessDelete } = useWorkflowListContext()

  const duplications = useLocalObservable(() => new Set<string>())
  const deletions = useLocalObservable(() => new Set<string>())

  const makeEditAction = (workflow: WorkflowRow): ActionItem => ({
    icon: 'edit',
    text: 'Edit',
    onAction: () => {
      uiStore.changeRoute({ path: `/workflows/view/${workflow.id}` })
    },
  })

  const makeDuplicateAction = (workflow: WorkflowRow): ActionItem => ({
    icon: 'copy',
    text: 'Duplicate',
    buttonProps: {
      disabled: duplications.has(workflow.id) || deletions.has(workflow.id),
      loading: duplications.has(workflow.id),
    },
    onAction: () => {
      duplications.add(workflow.id)
      workflowOperation.duplicate(workflow).then((duplicated) => {
        duplications.delete(workflow.id)

        if (duplicated) uiStore.changeRoute({ path: `/workflows/view/${duplicated.id}` })
      })
    },
  })

  const makeDeleteAction = (workflow: WorkflowRow): ActionItem => ({
    icon: 'delete',
    text: 'Delete',
    buttonProps: {
      disabled: deletions.has(workflow.id) || duplications.has(workflow.id),
      loading: deletions.has(workflow.id),
    },
    onAction: () => {
      deletions.add(workflow.id)
      workflowOperation.delete(workflow).then((deleted) => {
        deletions.delete(workflow.id)

        if (deleted) onSuccessDelete(workflow.id)
      })
    },
  })

  const columns = COLUMNS
  const rows = items.map((row) => {
    row.setActionsProps({
      actions: [
        makeEditAction(row.workflow),
        makeDuplicateAction(row.workflow),
        makeDeleteAction(row.workflow),
      ],
    })
    return row
  })

  return { rows, columns }
}
